import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ShieldCheck, HeartPulse, Sparkles, ChevronLeft, RotateCcw, Moon } from "lucide-react";
import { VoiceButton } from "@/components/VoiceButton";
import { APP_URL, COACH_CATEGORIES } from "@/data/landing";
import { slugify } from "@/components/landing/primitives";

const QUIZ_GOALS = [
  { key: "calmer", label: "Feel calmer day-to-day", icon: Sparkles },
  { key: "sleep", label: "Sleep more easily", icon: Moon },
  { key: "in-control", label: "Feel in control again", icon: ShieldCheck },
  { key: "self-compassion", label: "Be kinder to myself", icon: HeartPulse },
];

const QUIZ_CAT_ICON = { anxiety: HeartPulse, ptsd: ShieldCheck, postpartum: Sparkles };

const stepVariants = {
  enter: { opacity: 0, x: 24 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};

const STEP_TRANSITION = { duration: 0.3 };

export const CoachQuiz = () => {
  const [step, setStep] = useState(1);
  const [cat, setCat] = useState(null);
  const [coach, setCoach] = useState(null);
  const [goal, setGoal] = useState(null);
  const total = 3;

  const reset = () => { setStep(1); setCat(null); setCoach(null); setGoal(null); };

  const appLink = coach && cat
    ? `${APP_URL}?focus=${cat.value}&coach=${slugify(coach.name)}&voice=${slugify(coach.voice)}${goal ? `&goal=${goal.key}` : ""}`
    : APP_URL;

  useEffect(() => {
    if (!cat || !coach) return;
    sessionStorage.setItem("afwn_quiz", JSON.stringify({
      focus: cat.value, coach: slugify(coach.name), voice: slugify(coach.voice), goal: goal?.key || "",
    }));
  }, [cat, coach, goal]);

  return (
    <div data-testid="coach-quiz" className="relative bg-white rounded-[2rem] border border-afwn-navy/10 shadow-xl shadow-afwn-navy/5 p-6 sm:p-10 max-w-4xl mx-auto overflow-hidden">
      {step <= total && (
        <div className="flex items-center justify-between mb-8">
          <p className="text-xs tracking-wider uppercase font-semibold text-afwn-slate">Step {step} of {total}</p>
          <div className="flex gap-1.5">
            {[1, 2, 3].map((n) => (
              <span key={n} className={`h-1.5 rounded-full transition-all duration-300 ${n <= step ? "w-8 bg-afwn-green" : "w-4 bg-afwn-navy/15"}`} />
            ))}
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={STEP_TRANSITION}>
            <h3 className="font-heading font-light text-3xl sm:text-4xl text-afwn-navy text-center">What brings you here today?</h3>
            <p className="text-afwn-slate text-center mt-2">We&rsquo;ll match you with the right assistant.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {COACH_CATEGORIES.map((c) => {
                const Icon = QUIZ_CAT_ICON[c.value];
                return (
                  <button
                    key={c.value}
                    data-testid={`quiz-cat-${c.value}`}
                    onClick={() => { setCat(c); setCoach(null); setStep(2); }}
                    className="group rounded-2xl border-2 p-6 text-left transition-all duration-300 hover:-translate-y-1 focus:outline-none"
                    style={{ borderColor: `${c.color}33` }}
                  >
                    <span className="w-11 h-11 rounded-full flex items-center justify-center text-white mb-4" style={{ background: c.color }}><Icon size={20} /></span>
                    <p className="font-heading text-2xl" style={{ color: c.color }}>{c.label}</p>
                    <p className="text-sm text-afwn-slate mt-1 leading-snug">{c.focus}</p>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {step === 2 && cat && (
          <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={STEP_TRANSITION}>
            <h3 className="font-heading font-light text-3xl sm:text-4xl text-afwn-navy text-center">Choose your {cat.label} assistant</h3>
            <p className="text-afwn-slate text-center mt-2">Pick the presence that feels right for you.</p>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {cat.coaches.map((co) => (
                <button
                  key={co.name}
                  data-testid={`quiz-coach-${co.name.toLowerCase()}`}
                  onClick={() => { setCoach(co); setStep(3); }}
                  className="group rounded-2xl border-2 overflow-hidden text-left transition-all duration-300 hover:-translate-y-1 focus:outline-none"
                  style={{ borderColor: `${cat.color}22` }}
                >
                  <img src={co.img} alt={co.name} className="w-full aspect-square object-cover" />
                  <div className="p-3">
                    <p className="font-heading text-xl leading-none" style={{ color: cat.color }}>{co.name}</p>
                    <p className="text-xs text-afwn-slate mt-1">{co.voice}</p>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={STEP_TRANSITION}>
            <h3 className="font-heading font-light text-3xl sm:text-4xl text-afwn-navy text-center">What&rsquo;s your first goal?</h3>
            <p className="text-afwn-slate text-center mt-2">We&rsquo;ll help {coach ? coach.name : "your assistant"} focus your first week.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {QUIZ_GOALS.map((g) => {
                const Icon = g.icon;
                return (
                  <button
                    key={g.key}
                    data-testid={`quiz-goal-${g.key}`}
                    onClick={() => { setGoal(g); setStep(4); }}
                    className="flex items-center gap-3 rounded-2xl border-2 border-afwn-navy/10 p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:border-afwn-green focus:outline-none"
                  >
                    <span className="w-10 h-10 rounded-full bg-afwn-green/10 flex items-center justify-center text-afwn-green shrink-0"><Icon size={18} /></span>
                    <span className="text-lg text-afwn-navy">{g.label}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {step === 4 && coach && cat && (
          <motion.div key="result" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={STEP_TRANSITION} className="text-center">
            <div className="inline-block relative">
              <img src={coach.img} alt={coach.name} className="w-28 h-28 rounded-full object-cover mx-auto border-4" style={{ borderColor: cat.color }} />
              <span className="absolute bottom-1 right-1 w-7 h-7 rounded-full flex items-center justify-center text-white ring-2 ring-white" style={{ background: cat.color }}><Check size={15} /></span>
            </div>
            <h3 className="mt-5 font-heading font-light text-3xl sm:text-4xl text-afwn-navy">Meet {coach.name}, your <span className="italic" style={{ color: cat.color }}>{coach.voice.toLowerCase()}</span> guide.</h3>
            <p className="mt-3 text-afwn-slate max-w-md mx-auto text-lg">
              Specialized in {cat.label} support{goal ? `, ready to help you ${goal.label.toLowerCase()}` : ""}. Your 7-day free trial is set up and waiting.
            </p>
            <div className="mt-5 flex justify-center">
              <VoiceButton slug={slugify(coach.name)} name={coach.name} color={cat.color} />
            </div>
            <div className="mt-8 flex flex-col items-center gap-3">
              <a
                href={appLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quiz-start-button"
                className="inline-flex items-center justify-center gap-2 rounded-full font-medium px-8 py-4 text-base text-white shadow-lg transition-all duration-300 hover:scale-[1.03] focus:outline-none"
                style={{ background: cat.color }}
              >
                Start with {coach.name} <ArrowRight size={18} />
              </a>
              <button data-testid="quiz-restart" onClick={reset} className="inline-flex items-center gap-1.5 text-sm text-afwn-slate hover:text-afwn-green transition-colors">
                <RotateCcw size={14} /> Start over
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {step > 1 && step <= total && (
        <button data-testid="quiz-back" onClick={() => setStep(step - 1)} className="mt-8 inline-flex items-center gap-1 text-sm text-afwn-slate hover:text-afwn-green transition-colors">
          <ChevronLeft size={16} /> Back
        </button>
      )}
    </div>
  );
};
